import React, { useState, useMemo, useEffect } from 'react';
import DataTable from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { fetchCandidates, disableAccount, resetPassword, type AdminCandidate } from '../lib/userService';
import { logger } from '../lib/logger';

const statusLabels: Record<string, string> = {
  active: 'Actif',
  pending_verification: 'En attente',
  pending_kyc: 'KYC en cours',
  suspended: 'Suspendu',
  rejected: 'Rejeté',
};

const statusClasses: Record<string, string> = {
  active: 'bg-blue-50 text-blue-700 border border-blue-200',
  pending_verification: 'bg-amber-50 text-amber-700 border border-amber-200',
  pending_kyc: 'bg-amber-50 text-amber-700 border border-amber-200',
  suspended: 'bg-red-50 text-red-700 border border-red-200',
  rejected: 'bg-gray-100 text-gray-500 border border-gray-200',
};

const Candidates: React.FC = () => {
  const [candidates, setCandidates] = useState<AdminCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [seedFilter, setSeedFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [resetModal, setResetModal] = useState<AdminCandidate | null>(null);
  const [newPwd, setNewPwd] = useState('');

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCandidates()
      .then(setCandidates)
      .catch((err) => {
        logger.error('load_candidates', 'Candidates', { error: String(err) });
        setError(String(err));
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return candidates.filter((c) => {
      const matchSearch =
        search === '' ||
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
        c.city.toLowerCase().includes(search.toLowerCase()) ||
        c.email.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || c.status === statusFilter;
      const matchSeed = seedFilter === 'all' || (seedFilter === 'seed' ? !!c.seedTag : !c.seedTag);
      return matchSearch && matchStatus && matchSeed;
    });
  }, [candidates, search, statusFilter, seedFilter]);

  const handleToggleSuspend = async (c: AdminCandidate) => {
    setActionLoading(c.userId);
    try {
      const willSuspend = c.status !== 'suspended';
      await disableAccount(c.userId, willSuspend);
      setCandidates((prev) =>
        prev.map((x) =>
          x.userId === c.userId ? { ...x, status: willSuspend ? 'suspended' : 'active' } : x
        )
      );
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPwd = async () => {
    if (!resetModal || !newPwd) return;
    setActionLoading(resetModal.userId);
    try {
      await resetPassword(resetModal.userId, newPwd);
      alert('Mot de passe réinitialisé avec succès.');
      setResetModal(null);
      setNewPwd('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const columns: Column<AdminCandidate>[] = [
    {
      key: 'photo',
      header: '',
      width: '50px',
      render: (c) => (
        <div className="w-9 h-9 rounded-full bg-[#365A8C] flex items-center justify-center text-white text-xs font-bold">
          {c.firstName?.[0] || ''}{c.lastName?.[0] || ''}
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Nom',
      render: (c) => (
        <div>
          <div className="flex items-center gap-1.5">
            <p className="font-medium text-gray-900">{c.firstName} {c.lastName}</p>
            {c.seedTag && <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-indigo-100 text-indigo-700 uppercase">seed</span>}
          </div>
          <p className="text-xs text-gray-400">{c.email}</p>
        </div>
      ),
    },
    {
      key: 'city',
      header: 'Ville',
      render: (c) => <span className="text-gray-600">{c.city || '—'}</span>,
    },
    {
      key: 'status',
      header: 'Statut',
      render: (c) => (
        <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${statusClasses[c.status] || 'bg-gray-100 text-gray-500'}`}>
          {statusLabels[c.status] || c.status}
        </span>
      ),
    },
    {
      key: 'registeredAt',
      header: 'Inscrit le',
      render: (c) => <span className="text-gray-500">{c.registeredAt}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (c) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleToggleSuspend(c)}
            disabled={actionLoading === c.userId}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              c.status === 'suspended'
                ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                : 'text-orange-600 bg-orange-50 hover:bg-orange-100'
            } disabled:opacity-40`}
          >
            {c.status === 'suspended' ? 'Réactiver' : 'Suspendre'}
          </button>
          <button
            onClick={() => { setResetModal(c); setNewPwd(''); }}
            className="px-2.5 py-1 text-xs font-medium text-[#00244F] bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
          >
            Mdp
          </button>
        </div>
      ),
    },
  ];

  const filterSlot = (
    <div className="flex items-center gap-2">
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
      >
        <option value="all">Tous les statuts</option>
        <option value="active">Actif</option>
        <option value="pending_verification">En attente</option>
        <option value="suspended">Suspendu</option>
      </select>
      <select
        value={seedFilter}
        onChange={(e) => setSeedFilter(e.target.value)}
        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
      >
        <option value="all">Tous (réels + seeds)</option>
        <option value="real">Réels uniquement</option>
        <option value="seed">Seeds uniquement</option>
      </select>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="bg-white rounded-xl border border-red-200 p-6 max-w-md text-center">
          <p className="text-red-600 font-medium mb-2">Erreur de chargement</p>
          <p className="text-sm text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => { setError(null); setLoading(true); fetchCandidates().then(setCandidates).catch((e) => setError(String(e))).finally(() => setLoading(false)); }}
            className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">👥</span>
          <div>
            <p className="text-xl font-bold text-gray-900">{candidates.length}</p>
            <p className="text-xs text-gray-500">Total candidats</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-blue-200 px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">✅</span>
          <div>
            <p className="text-xl font-bold text-blue-600">{candidates.filter(c => c.status === 'active').length}</p>
            <p className="text-xs text-gray-500">Actifs</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-red-200 px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">🚫</span>
          <div>
            <p className="text-xl font-bold text-red-600">{candidates.filter(c => c.status === 'suspended').length}</p>
            <p className="text-xs text-gray-500">Suspendus</p>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        keyExtractor={(c) => c.id}
        searchable
        searchPlaceholder="Rechercher par nom, email, ville..."
        onSearch={setSearch}
        filterSlot={filterSlot}
        pageSize={10}
      />

      {resetModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setResetModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Réinitialiser le mot de passe</h3>
              <p className="text-sm text-gray-500 mt-1">{resetModal.firstName} {resetModal.lastName} ({resetModal.email})</p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
              <input
                type="text"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Minimum 6 caractères"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setResetModal(null)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">
                Annuler
              </button>
              <button
                onClick={handleResetPwd}
                disabled={newPwd.length < 6 || actionLoading === resetModal.userId}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Candidates;
